import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    ControlsPageCommonFilters,
    PerCategoryFilter,
    UserOrGroup,
    StandardAccessRight,
} from "api/generated/model";
import { OrFilter, IdAndName, ControlsPageFilters } from "org/pageState/models";
import { ASSIGNEE_CATEGORY_METHODS } from "utils/assigneeTags";
import { DEFAULT_CATEGORY_METHODS } from "utils/tag";

import {
    decodeOrFilter,
    decodeOrFilterAsDate,
    encodeMatchRule,
    encodeOrFilter,
    encodeOrFilterAsDate,
} from "../common";

function decodeControlsPageCommonFilters(
    commonFilters: ControlsPageCommonFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];

    const decode = <T>(
        extractorId: string,
        filter: OrFilter<T> | null | undefined
    ) => {
        if (filter) {
            output.push(decodeOrFilter(extractorId, filter));
        }
    };

    const decodeDate = (
        extractorId: string,
        filter: OrFilter<string> | null | undefined
    ) => {
        if (filter) {
            output.push(decodeOrFilterAsDate(extractorId, filter));
        }
    };

    const decodeTags = (
        tags: Record<string, PerCategoryFilter> | null | undefined
    ) => {
        if (tags) {
            const tagFilters = decodeTagsDefault(tags);
            output.push(...tagFilters);
        }
    };

    const decodeAssigneeTags = (
        tags: Record<string, PerCategoryFilter> | null | undefined
    ) => {
        if (tags) {
            const tagFilters = decodeTagsAssignee(tags);
            output.push(...tagFilters);
        }
    };

    for (const k in commonFilters) {
        if ({}.hasOwnProperty.call(commonFilters, k)) {
            const key = k as keyof typeof commonFilters;
            switch (key) {
                case "name":
                    decode("name", commonFilters.name);
                    break;
                case "description":
                    decode("description", commonFilters.description);
                    break;
                case "program":
                    decode("program", commonFilters.program);
                    break;
                case "status":
                    decode("status", commonFilters.status);
                    break;
                case "loginProtection":
                    decode("loginProtection", commonFilters.loginProtection);
                    break;
                case "segregationOfDuties":
                    decode(
                        "segregationOfDuties",
                        commonFilters.segregationOfDuties
                    );
                    break;
                case "tagCategories":
                    decode("tagCategories", commonFilters.tagCategories);
                    break;
                case "accessRight":
                    decode("accessRight", commonFilters.accessRight);
                    break;
                case "assignee":
                    decode("onlySearchAssigneeName", commonFilters.assignee);
                    break;
                case "reviewer":
                    decode(
                        "onlySearchFirstReviewerName",
                        commonFilters.reviewer
                    );
                    break;
                case "secondReviewer":
                    decode(
                        "onlySearchSecondReviewerName",
                        commonFilters.secondReviewer
                    );
                    break;
                case "assigneeTagCategories":
                    decode(
                        "assigneeTagCategories",
                        commonFilters.assigneeTagCategories
                    );
                    break;
                case "tags":
                    decodeTags(commonFilters.tags);
                    break;
                case "assigneeTags":
                    decodeAssigneeTags(commonFilters.assigneeTags);
                    break;
                case "controlResultCount":
                    decode(
                        "controlResultCount",
                        commonFilters.controlResultCount
                    );
                    break;
                case "nextDueDate":
                    decodeDate("nextDueDate", commonFilters.nextDueDate);
                    break;
                case "entity":
                    decode("onlySearchEntityName", commonFilters.entity);
                    break;
                default: {
                    assertNever(key);
                }
            }
        }
    }
    return output;
}

function encodeControlsPageCommonFilters(
    filters: Array<SimpleOrFilter>
): ControlsPageCommonFilters {
    return {
        name: encodeOrFilter<IdAndName<string, string>>("name", filters),
        description: encodeOrFilter<string>("description", filters),
        program: encodeOrFilter<IdAndName<number, string>>("program", filters),
        status: encodeOrFilter<boolean>("status", filters),
        loginProtection: encodeOrFilter<boolean>("loginProtection", filters),
        segregationOfDuties: encodeOrFilter<boolean>(
            "segregationOfDuties",
            filters
        ),
        tagCategories: encodeOrFilter<IdAndName<string, string>>(
            "tagCategories",
            filters
        ),
        tags: encodeTagsDefault(filters),
        accessRight: encodeOrFilter<StandardAccessRight>(
            "accessRight",
            filters
        ),
        assignee: encodeOrFilter<UserOrGroup>(
            "onlySearchAssigneeName",
            filters
        ),
        reviewer: encodeOrFilter<UserOrGroup>(
            "onlySearchFirstReviewerName",
            filters
        ),
        secondReviewer: encodeOrFilter<UserOrGroup>(
            "onlySearchSecondReviewerName",
            filters
        ),
        assigneeTagCategories: encodeOrFilter<IdAndName<string, string>>(
            "assigneeTagCategories",
            filters
        ),
        assigneeTags: encodeTagsAssignee(filters),
        controlResultCount: encodeOrFilter<number>(
            "controlResultCount",
            filters
        ),
        nextDueDate: encodeOrFilterAsDate("nextDueDate", filters),
        entity: encodeOrFilter<IdAndName<number, string>>(
            "onlySearchEntityName",
            filters
        ),
    };
}

function decodeTagsDefault(
    tagFiltersPerCategoryId: Record<string, PerCategoryFilter>
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];

    for (const [catId, perCategoryFilter] of Object.entries(
        tagFiltersPerCategoryId
    )) {
        const { categoryName, filter } = perCategoryFilter;
        const extractorId: string =
            DEFAULT_CATEGORY_METHODS.abstractTagCategoryToExtractorId({
                id: catId,
                name: categoryName,
            });
        output.push(decodeOrFilter(extractorId, filter));
    }

    return output;
}

function decodeTagsAssignee(
    tagFiltersPerCategoryId: Record<string, PerCategoryFilter>
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];

    for (const [catId, perCategoryFilter] of Object.entries(
        tagFiltersPerCategoryId
    )) {
        const { categoryName, filter } = perCategoryFilter;
        const extractorId: string =
            ASSIGNEE_CATEGORY_METHODS.abstractTagCategoryToExtractorId({
                id: catId,
                name: categoryName,
            });
        output.push(decodeOrFilter(extractorId, filter));
    }

    return output;
}

function encodeTagsDefault(
    filters: Array<SimpleOrFilter>
): Record<string, PerCategoryFilter> {
    const output: Record<string, PerCategoryFilter> = {};

    const categoryFilters = filters.filter((f) =>
        DEFAULT_CATEGORY_METHODS.isCategoryExtractorId(f.extractorId)
    );
    for (const categoryFilter of categoryFilters) {
        const { id } =
            DEFAULT_CATEGORY_METHODS.extractorIdToAbstractTagCategory(
                categoryFilter.extractorId
            );
        const encodedFilter = encodePerCategoryFilterDefault(
            categoryFilter.extractorId,
            filters
        );

        if (encodedFilter) {
            output[id] = encodedFilter;
        }
    }

    return output;
}

function encodePerCategoryFilterDefault(
    extractorId: string,
    filters: Array<SimpleOrFilter>
): PerCategoryFilter | undefined {
    const relevantFilter = filters.find(
        (f) => f.extractorId === extractorId
    ) as SimpleOrFilter<IdAndName<string, string>> | undefined;
    if (!relevantFilter) {
        return undefined;
    }

    const includeFilters = relevantFilter.filters ? relevantFilter.filters : [];
    const excludeFilters = relevantFilter.exclusionFilters
        ? relevantFilter.exclusionFilters
        : [];

    const { name } =
        DEFAULT_CATEGORY_METHODS.extractorIdToAbstractTagCategory(extractorId);

    const filter: OrFilter<IdAndName<string, string>> = {
        include: includeFilters.map(encodeMatchRule),
        exclude: excludeFilters.map(encodeMatchRule),
    };

    return { categoryName: name, filter };
}

function encodeTagsAssignee(
    filters: Array<SimpleOrFilter>
): Record<string, PerCategoryFilter> {
    const output: Record<string, PerCategoryFilter> = {};

    const categoryFilters = filters.filter((f) =>
        ASSIGNEE_CATEGORY_METHODS.isCategoryExtractorId(f.extractorId)
    );
    for (const categoryFilter of categoryFilters) {
        const { id } =
            ASSIGNEE_CATEGORY_METHODS.extractorIdToAbstractTagCategory(
                categoryFilter.extractorId
            );
        const encodedFilter = encodePerCategoryFilterAssignee(
            categoryFilter.extractorId,
            filters
        );

        if (encodedFilter) {
            output[id] = encodedFilter;
        }
    }

    return output;
}

function encodePerCategoryFilterAssignee(
    extractorId: string,
    filters: Array<SimpleOrFilter>
): PerCategoryFilter | undefined {
    const relevantFilter = filters.find(
        (f) => f.extractorId === extractorId
    ) as SimpleOrFilter<IdAndName<string, string>> | undefined;
    if (!relevantFilter) {
        return undefined;
    }

    const includeFilters = relevantFilter.filters ? relevantFilter.filters : [];
    const excludeFilters = relevantFilter.exclusionFilters
        ? relevantFilter.exclusionFilters
        : [];

    const { name } =
        ASSIGNEE_CATEGORY_METHODS.extractorIdToAbstractTagCategory(extractorId);

    const filter: OrFilter<IdAndName<string, string>> = {
        include: includeFilters.map(encodeMatchRule),
        exclude: excludeFilters.map(encodeMatchRule),
    };

    return { categoryName: name, filter };
}

// --- You're entering the realm of Higher Order Functions ---

// Receives a decoder for a page specific filters and returns a function that decodes all filters (including common filters).
//
// Example:
//     const decodeQuarterlyFilters = createDecodePageFilters(decodeQuarterlySpecificFilters);
//
type DecodePageFiltersFn<SF> = (
    pageFilters: ControlsPageFilters<SF>
) => Array<SimpleOrFilter>;
type DecodeSpecificFiltersFn<SF> = (
    specificFilters: SF
) => Array<SimpleOrFilter>;
function createDecodePageFiltersFn<SF>(
    decodeSpecificFilters: DecodeSpecificFiltersFn<SF>
): DecodePageFiltersFn<SF> {
    return (pageFilters: ControlsPageFilters<SF>): Array<SimpleOrFilter> => {
        const { common, specific } = pageFilters;
        const commonFilters = decodeControlsPageCommonFilters(common);
        const specificFilters = decodeSpecificFilters(specific);
        return commonFilters.concat(specificFilters);
    };
}

// Creates a guard function from an array of valid values, to verify that a given string matches the type.
//
// Example:
//     type Abc = "A" | "B" | "C"
//     const ALL_ABC: Abc[] = ["A", "B", "C"];
//     const isAbc = createIsColumnTypeFn(ALL_ABC);
//
//     const a: string = "A";
//     if (isAbc(a)) {
//         // here `a` is proven to be a valid Abc type.
//     }
//
type IsColumnTypeFn<C extends string> = (columnId: string) => columnId is C;
function createIsColumnTypeFn<C extends string>(
    validColumnIds: C[]
): IsColumnTypeFn<C> {
    return (columnId: string): columnId is C => {
        const validColumns: string[] = validColumnIds;
        if (validColumns.includes(columnId)) {
            return true;
        } else {
            return false;
        }
    };
}

// Creates a encoder for a column (specified with array of valid values).
// The encoder receives a string, proves that it contains a valid column and returns back typed as a column.
// Otherwise throws an error.
//
// Example:
//     type Abc = "A" | "B" | "C";
//     const ALL_ABC: Abc[] = ["A", "B", "C"];
//     const encodeAbcColumn = createEncodeColumnFn(ALL_ABC);
//
//     // returns A typed as Abc
//     encodeAbcColumn("A")
//
//     // throws an error
//     encodeAbcColumn("Y")
//
type EncodeColumnFn<C extends string> = (visibleColumnId: string) => C;
function createEncodeColumnFn<C extends string>(
    validColumns: C[]
): EncodeColumnFn<C> {
    const isColumnType = createIsColumnTypeFn(validColumns);

    return (visibleColumnId: string) => {
        if (isColumnType(visibleColumnId)) {
            return visibleColumnId;
        } else {
            throw new Error(`Unknown visibleColumnId: ${visibleColumnId}`);
        }
    };
}

// Creates a encoder of filters for a particular pages
// The function receives an encoder of specific filters and returns a function that encodes all page filters (including common).
//
type EncodePageFiltersFn<SF> = (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsPageFilters<SF>;
type EncodeSpecificFiltersFn<SF> = (filters: Array<SimpleOrFilter>) => SF;
function createEncodePageFiltersFn<SF>(
    encodeSpecificFilters: EncodeSpecificFiltersFn<SF>
): EncodePageFiltersFn<SF> {
    return (
        simpleAndFilter: SimpleAndFilter | null
    ): ControlsPageFilters<SF> => {
        let filters: Array<SimpleOrFilter> = [];
        if (simpleAndFilter) {
            filters = simpleAndFilter.filters;
        }
        return {
            common: encodeControlsPageCommonFilters(filters),
            specific: encodeSpecificFilters(filters),
        };
    };
}

export {
    decodeControlsPageCommonFilters,
    encodeControlsPageCommonFilters,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
    createDecodePageFiltersFn,
};
